export interface Product {
  id: string;
  name: string;
  description: string;
  price: string;
  priceNote?: string;
  category: string;
  image: string;
  images?: string[]; // carousel — if set, overrides single image
  features: string[];
  compatibleModels?: string[];
}

export interface BrandData {
  slug: string;
  name: string;
  fullName: string;
  tagline: string;
  heroImage: string;
  brandImage: string;
  models: string[];
  categories: string[];
  products: Product[];
}

export const brands: BrandData[] = [
  {
    slug: 'megane-3',
    name: 'Mégane 3',
    fullName: 'Renault Mégane 3 RS',
    tagline: 'Dry carbon components for the legendary hot hatch. Precision-engineered for every Mégane 3 RS model.',
    heroImage: '/images/megan 3/71E0B80B-AFAF-4DCF-A4BE-3A1F3E2DAF6E.jpg',
    brandImage: '/images/megan 3/IMG_1639.JPG',
    models: ['Mégane RS 250', 'Mégane RS 265', 'Mégane RS 275 Trophy', 'Mégane RS 275 Trophy-R'],
    categories: ['Exhaust', 'Badges', 'Side Skirts', 'Door Handles', 'Spoiler'],
    products: [
      {
        id: 'megane3-auspuh',
        name: 'Carbon Exhaust Tip',
        description: 'Dry carbon exhaust tip with the Budzevina logo engraved on the oval exit. Bolt-on OEM fitment, zero welding required. Significant weight reduction over the stock steel tip.',
        price: '€180',
        category: 'Exhaust',
        image: '/images/megan 3/IMG_1646.JPG',
        features: ['Dry carbon technology', 'Budzevina logo engraved', 'OEM bolt-on fit', 'No welding required'],
        compatibleModels: ['Mégane RS 250', 'Mégane RS 265', 'Mégane RS 275 Trophy', 'Mégane RS 275 Trophy-R'],
      },
      {
        id: 'megane3-prednji-znak',
        name: 'Carbon Front Badge',
        description: 'Dry carbon front badge for the Mégane 3 RS. Direct clip-on replacement for the OEM plastic badge — zero modification, zero drilling, instant premium look.',
        price: '€80',
        category: 'Badges',
        image: 'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.14.58-2.jpeg',
        features: ['Dry carbon technology', 'Direct OEM replacement', 'No drilling required', 'Gloss finish'],
        compatibleModels: ['Mégane RS 250', 'Mégane RS 265', 'Mégane RS 275 Trophy', 'Mégane RS 275 Trophy-R'],
      },
      {
        id: 'megane3-zadnji-znak',
        name: 'Carbon Rear Badge',
        description: 'Dry carbon rear badge for the Mégane 3 RS. Clips directly over the OEM rear badge — no tools needed. Complements the exhaust tip perfectly.',
        price: '€80',
        category: 'Badges',
        image: 'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.14.58-1.jpeg',
        features: ['Dry carbon technology', 'Direct OEM replacement', 'No tools required', 'Gloss finish'],
        compatibleModels: ['Mégane RS 250', 'Mégane RS 265', 'Mégane RS 275 Trophy', 'Mégane RS 275 Trophy-R'],
      },
      {
        id: 'megane3-sajtne',
        name: 'Carbon Side Skirts',
        description: 'Dry carbon side skirts for the Mégane 3 RS. Clip-on OEM fit — no drilling or cutting. Visually lowers the car and reduces underbody turbulence at speed.',
        price: '€120',
        category: 'Side Skirts',
        image: 'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.14.58.jpeg',
        features: ['Dry carbon technology', 'Clip-on OEM fit', 'No drilling required', 'Left + right included'],
        compatibleModels: ['Mégane RS 250', 'Mégane RS 265', 'Mégane RS 275 Trophy', 'Mégane RS 275 Trophy-R'],
      },
      {
        id: 'megane3-kvake',
        name: 'Carbon Door Handles',
        description: 'Dry carbon door handle covers for the Mégane 3 RS. Precision-molded pair — clips directly over OEM handles, no adhesive or drilling. Instant premium tactile upgrade.',
        price: '€180',
        priceNote: 'price per pair',
        category: 'Door Handles',
        image: 'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-22.00.00.jpeg',
        features: ['Dry carbon technology', 'Clip-on OEM fit', 'No drilling required', 'Pair (left + right)'],
        compatibleModels: ['Mégane RS 250', 'Mégane RS 265', 'Mégane RS 275 Trophy', 'Mégane RS 275 Trophy-R'],
      },
      {
        id: 'megane3-big-wing',
        name: 'Carbon Rear Wing',
        description: 'Full-size dry carbon rear wing for the Mégane 3 RS. Maximum downforce, full circuit presence. Mounts directly to OEM boot-lid points — no cutting or drilling required.',
        price: '€1,300',
        category: 'Spoiler',
        image: '/images/megan 3/71E0B80B-AFAF-4DCF-A4BE-3A1F3E2DAF6E.jpg',
        features: ['Dry carbon technology', 'Maximum downforce', 'OEM boot-lid mounting', 'Hardware included'],
        compatibleModels: ['Mégane RS 250', 'Mégane RS 265', 'Mégane RS 275 Trophy', 'Mégane RS 275 Trophy-R'],
      },
    ],
  },
  {
    slug: 'megane-4',
    name: 'Mégane 4',
    fullName: 'Renault Mégane 4 RS',
    tagline: 'Precision dry carbon exhaust components for the latest generation Mégane RS.',
    heroImage: '/images/megan 4/IMG_1604.JPG',
    brandImage: '/images/megan 4/IMG_1602.JPG',
    models: ['Mégane RS 280', 'Mégane RS 300 Trophy', 'Mégane RS Trophy-R', 'Mégane R.S. EDC'],
    categories: ['Exhaust', 'Spoiler'],
    products: [
      {
        id: 'megane4-spoiler-trophy',
        name: 'Carbon Rear Spoiler — RS Trophy',
        description: 'Dry carbon rear spoiler made specifically for the Mégane 4 RS Trophy. Available in Regular or Forged carbon — choose your finish when ordering. OEM bolt-on fitment, no cutting required.',
        price: '€750',
        category: 'Spoiler',
        image: 'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.58.44-3.jpeg',
        images: [
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.58.44-3.jpeg',
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.58.44.jpeg',
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.58.44-1.jpeg',
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.58.44-2.jpeg',
        ],
        features: ['Regular or Forged carbon — your choice', 'RS Trophy specific fitment', 'OEM bolt-on, no cutting', 'Gloss finish'],
        compatibleModels: ['Mégane RS Trophy-R', 'Mégane RS 300 Trophy'],
      },
      {
        id: 'megane4-auspuh',
        name: 'Carbon Exhaust Cover Set — Stage 1',
        description: 'Dry carbon exhaust cover set for the Mégane 4 RS. Slimmer profile Stage 1 — replaces the stock covers with lightweight carbon units for a clean, aggressive rear end.',
        price: '€220',
        category: 'Exhaust',
        image: 'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.20.43.jpeg',
        images: [
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.20.43.jpeg',
        ],
        features: ['Dry carbon technology', 'Stage 1 slim profile', 'OEM fitment', 'Gloss finish'],
        compatibleModels: ['Mégane RS 280', 'Mégane RS 300 Trophy', 'Mégane RS Trophy-R', 'Mégane R.S. EDC'],
      },
      {
        id: 'megane4-auspuh-stage2',
        name: 'Carbon Exhaust Cover Set — Stage 2',
        description: 'Upgraded Stage 2 dry carbon exhaust cover set for the Mégane 4 RS. More aggressive outlet geometry, increased flow efficiency, and an even more commanding rear profile.',
        price: '€270',
        category: 'Exhaust',
        image: 'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.20.44-scaled.jpeg',
        images: [
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.20.44-scaled.jpeg',
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.20.43-1-scaled.jpeg',
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.20.43.jpeg',
        ],
        features: ['Dry carbon technology', 'Stage 2 aggressive profile', 'OEM fitment', 'Gloss finish'],
        compatibleModels: ['Mégane RS 280', 'Mégane RS 300 Trophy', 'Mégane RS Trophy-R', 'Mégane R.S. EDC'],
      },
    ],
  },
  {
    slug: 'porsche',
    name: 'Porsche',
    fullName: 'Porsche',
    tagline: 'Dry carbon intake and sensor components for one of the world\'s most celebrated sports cars.',
    heroImage: '/images/porsche/IMG_9626.JPG',
    brandImage: '/images/porsche/IMG_9627.JPG',
    models: ['911 GT3 / GT3 RS', '911 Turbo S', 'Cayman GT4', '718 Boxster S'],
    categories: ['Intake', 'Sensor Cover', 'Sensor Cover 911 Turbo'],
    products: [
      {
        id: 'porsche-usis',
        name: 'Carbon Intake Scoop Set',
        description: 'Dry carbon fiber intake scoops for the Porsche 911 rear fender. Precision-engineered pair — OEM mounting, zero modification. Maximum cold air flow directly into the engine bay.',
        price: '€500',
        priceNote: 'price per pair',
        category: 'Intake',
        image: '/images/porsche/IMG_9626.JPG',
        features: ['Dry carbon technology', 'OEM mounting, no mods', 'Pair (left + right)', 'Gloss finish'],
        compatibleModels: ['911 GT3', '911 GT3 RS', '911 Turbo S', 'Cayman GT4'],
      },
      {
        id: 'porsche-sensor-cover-turbo',
        name: 'Carbon Sensor Cover Set — 911 Turbo',
        description: 'Dry carbon sensor covers made specifically for the Porsche 911 Turbo. Direct-fit replacement for the OEM plastic bezels — supplied as a pair. Show-quality gloss finish.',
        price: '€350',
        priceNote: 'price per pair',
        category: 'Sensor Cover',
        image: 'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.23.17.jpeg',
        images: [
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.23.17.jpeg',
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.23.17-1.jpeg',
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/WhatsApp-Image-2026-06-14-at-21.23.17-2.jpeg',
        ],
        features: ['Dry carbon technology', 'Direct OEM replacement', 'Pair (left + right)', 'Gloss finish'],
        compatibleModels: ['911 Turbo', '911 Turbo S'],
      },
      {
        id: 'porsche-sensor-cover',
        name: 'Carbon Sensor Cover Set',
        description: 'Dry carbon sensor covers for the Porsche 911. Elegant replacement for the plastic OEM sensor bezels — direct-fit, supplied as a pair. Show-quality gloss finish.',
        price: '€250',
        priceNote: 'price per pair',
        category: 'Sensor Cover',
        image: '/images/porsche/IMG_9633.JPG',
        features: ['Dry carbon technology', 'Direct OEM replacement', 'Pair (left + right)', 'Gloss finish'],
        compatibleModels: ['911 GT3', '911 GT3 RS', '911 Turbo S', 'Cayman GT4', '718 Boxster S'],
      },
    ],
  },
  {
    slug: 'fiat-abarth',
    name: 'Fiat Abarth',
    fullName: 'Fiat 500 Abarth',
    tagline: 'Complete dry carbon exterior kit for the Fiat 500 Abarth — total visual transformation in one package.',
    heroImage: '/images/fiat 500 abarth/IMG_2919.jpeg',
    brandImage: '/images/fiat 500 abarth/IMG_2919.jpeg',
    models: ['500 Abarth 595', '500 Abarth 695', '500 Abarth 695 Biposto', '500 Abarth 695 XSR'],
    categories: ['Full Kit'],
    products: [
      {
        id: 'abarth-full-kit',
        name: 'Carbon Exterior Full Kit',
        description: 'Complete dry carbon exterior kit for the Fiat 500 Abarth. Includes door handle covers, door handle frames, front badge surround, rear badge surround, and grille strip. Total visual transformation, OEM fitment throughout.',
        price: '€650',
        category: 'Full Kit',
        image: '/images/fiat 500 abarth/IMG_2919.jpeg',
        features: ['Dry carbon technology', 'All exterior pieces included', 'OEM fitment', 'All hardware included'],
        compatibleModels: ['500 Abarth 595', '500 Abarth 695', '500 Abarth 695 Biposto', '500 Abarth 695 XSR'],
      },
    ],
  },
  {
    slug: 'aventador',
    name: 'Aventador',
    fullName: 'Lamborghini Aventador',
    tagline: 'Dry carbon interior and exterior components for the most dramatic supercar ever made.',
    heroImage: '/images/aventador/IMG_9968.JPG',
    brandImage: '/images/aventador/IMG_9966.JPG',
    models: ['Aventador LP700-4', 'Aventador S', 'Aventador SVJ', 'Aventador Ultimae'],
    categories: ['Interior', 'Door Sills'],
    products: [
      {
        id: 'aventador-konzola',
        name: 'Carbon Center Console Kit',
        description: 'Complete dry carbon center console kit for the Lamborghini Aventador. Every panel, switch surround, and toggle cover replaced with high-gloss dry carbon. Show-quality finish, zero compromise.',
        price: '€850',
        category: 'Interior',
        image: '/images/aventador/IMG_9966.JPG',
        features: ['Dry carbon technology', 'Complete panel set', 'Show-quality finish', 'All hardware included'],
        compatibleModels: ['Aventador LP700-4', 'Aventador S', 'Aventador SVJ', 'Aventador Ultimae'],
      },
      {
        id: 'aventador-pragovi',
        name: 'Carbon Door Sill Set',
        description: 'Dry carbon door sill plates for the Lamborghini Aventador with custom "AVENTADOR" lettering in orange. Fitted to OEM mounts — a bold entrance statement on every door opening.',
        price: '€250',
        priceNote: 'set of 2',
        category: 'Door Sills',
        image: '/images/aventador/4295e49e-8a17-468e-bf21-f13813a1b316.jpg',
        features: ['Dry carbon technology', '"AVENTADOR" orange lettering', 'OEM mounting points', 'Set of 2'],
        compatibleModels: ['Aventador LP700-4', 'Aventador S', 'Aventador SVJ', 'Aventador Ultimae'],
      },
    ],
  },
  {
    slug: 'mercedes-g',
    name: 'Mercedes G',
    fullName: 'Mercedes G-Class',
    tagline: 'Precision dry carbon interior sets for the most iconic luxury SUV ever built.',
    heroImage: '/images/g klasa/IMG_5174.JPG',
    brandImage: '/images/g klasa/IMG_5170.jpeg',
    models: ['G 500', 'G 63 AMG', 'G 350 d', 'G 400 d', 'G 63 AMG Edition 1'],
    categories: ['Interior', 'Ventilation'],
    products: [
      {
        id: 'g-interior-full',
        name: 'Carbon Interior Full Set',
        description: 'Complete dry carbon interior set for the Mercedes G-Class. Covers the entire dashboard console panel including ventilation surrounds, switch panels, and center stack — total cockpit transformation.',
        price: '€1,200',
        category: 'Interior',
        image: 'https://budzevinacarbon.com/wp-content/uploads/2026/06/IMG_5173.jpeg',
        images: [
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/IMG_5177.jpg',
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/IMG_5173.jpeg',
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/IMG_5174.jpg',
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/IMG_5172.jpeg',
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/IMG_5170.jpeg',
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/IMG_5171.jpeg',
          'https://budzevinacarbon.com/wp-content/uploads/2026/06/IMG_5180.jpg',
        ],
        features: ['Dry carbon technology', 'Full dashboard console', 'All panels included', 'OEM fitment'],
        compatibleModels: ['G 500', 'G 63 AMG', 'G 350 d', 'G 400 d', 'G 63 AMG Edition 1'],
      },
      {
        id: 'g-ventilacija',
        name: 'Carbon Ventilation Surround Set',
        description: 'Dry carbon ventilation surround set for the Mercedes G-Class. Replaces the OEM turbine vent housings with carbon units — direct fit, no modification. Glows dramatically with the ambient lighting.',
        price: '€750',
        category: 'Ventilation',
        image: '/images/g klasa/IMG_5177.JPG',
        features: ['Dry carbon technology', 'Direct OEM replacement', 'Works with ambient lighting', 'Complete set'],
        compatibleModels: ['G 500', 'G 63 AMG', 'G 350 d', 'G 400 d', 'G 63 AMG Edition 1'],
      },
    ],
  },
  {
    slug: 'custom',
    name: 'Custom',
    fullName: 'Custom Builds',
    tagline: 'Bespoke carbon fiber steering wheels made to your exact specification. Send us your wheel — we do the rest.',
    heroImage: '/images/aventador/IMG_9968.JPG',
    brandImage: '/images/aventador/IMG_9968.JPG',
    models: ['All Makes', 'All Models'],
    categories: ['Steering Wheels'],
    products: [
      {
        id: 'custom-volan',
        name: 'Custom Carbon Steering Wheel',
        description: 'Hand-crafted dry carbon steering wheel built to your exact specification. Send us your wheel and we can do whatever you want. Every wheel is one-of-a-kind — shape, spoke design, flat-bottom or round, stitch position, and finish. All makes and models accepted.',
        price: 'From €650',
        category: 'Steering Wheels',
        image: '/images/volani/WhatsApp%20Image%202026-06-13%20at%2012.36.40%20(2).jpeg',
        features: ['Dry carbon technology', 'Send us your wheel', 'Fully bespoke design', 'All makes and models'],
      },
    ],
  },
];

export function getBrandBySlug(slug: string): BrandData | undefined {
  return brands.find(b => b.slug === slug);
}
